import type { LeadRecord } from './wiz-leads.js';

export type CampaignType = 'email' | 'linkedin' | 'multi';

export interface Weekday {
  day: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  displayName: string;
}

export const WEEKDAYS: Weekday[] = [
  { day: 0, displayName: 'Mon' },
  { day: 1, displayName: 'Tue' },
  { day: 2, displayName: 'Wed' },
  { day: 3, displayName: 'Thu' },
  { day: 4, displayName: 'Fri' },
  { day: 5, displayName: 'Sat' },
  { day: 6, displayName: 'Sun' },
];

export interface SourceDocument {
  fileName: string;
  uploadedAt: string;
  url?: string;
  source?: 'local' | 'gdrive' | 'onedrive' | 'website';
}

export interface OfferingConfig {
  name: string;
  description: string;
  talkingPoints: string[];
  sourceDocuments?: SourceDocument[];
}

// ---------------------------------------------------------------------------
// Document Analysis types (AI Expert Agent)
// ---------------------------------------------------------------------------

export type AnalysisPhase = 'idle' | 'analyzing' | 'questions' | 'generating' | 'result';

export interface AnalysisQuestion {
  id: string;
  question: string;
  options: string[];
  why: string;
}

export interface AnalysisClassification {
  domain: string;
  product: string[];
  background: string[];
  competitive: string[];
}

export interface AnalyzeResponse {
  productName: string;
  nameConfidence?: 'high' | 'low';
  alternativeNames?: string[];
  classification: AnalysisClassification;
  questions: AnalysisQuestion[];
}

export interface AnalysisResult {
  productName: string;
  productDescription: string;
  talkingPoints: string[];
}

export interface ScheduleConfig {
  timezone: string;
  startTime: Date;
  endTime: Date;
  sendDays: Weekday[];
  dailyLimit: number;
  autoDmOnAccept: boolean;
}

// ---------------------------------------------------------------------------
// Sequence Builder types
// ---------------------------------------------------------------------------
export type SequenceChannel = 'email' | 'linkedin_connect' | 'linkedin_dm';
export type SequenceCondition = 'always' | 'no_reply' | 'accepted' | 'not_accepted' | 'replied';
export type DelayUnit = 'minutes' | 'hours' | 'days';

/**
 * Whether a LinkedIn connect step actually carries a note to lose.
 *
 * The note IS the step's message — manual text if the user wrote one, the AI
 * prompt otherwise, since that is what a generated note comes from. A step with
 * neither will send a bare invitation whatever LinkedIn decides, so there is
 * nothing to warn anyone about.
 *
 * Lives here rather than in the notice because both the step card and the
 * sequence header ask it, and two answers to one question is how a banner comes
 * to contradict the card underneath it.
 */
export function connectStepCarriesANote(step: {
  channel: string;
  prompt?: string;
  manualBody?: string;
  previewBody?: string;
}): boolean {
  if (step.channel !== 'linkedin_connect') return false;
  return Boolean((step.manualBody ?? '').trim() || (step.prompt ?? '').trim() || (step.previewBody ?? '').trim());
}

export interface SequenceStep {
  id: string;
  channel: SequenceChannel;
  delayValue: number;
  delayUnit: DelayUnit;
  condition: SequenceCondition;
  prompt: string;
  subject: string;
  /**
   * Optional AI prompt that describes how the subject line should be written.
   * If set, the LLM generates the subject following this guidance instead of
   * using the literal `subject` template.
   */
  subjectPrompt?: string;
  /**
   * Manual message mode: when set, this exact text (with {{variables}}
   * interpolated per lead) is used as the message body — the LLM is skipped
   * entirely for this step. Cleared to return to AI generation.
   */
  manualBody?: string;
  /**
   * Last generated sample message (the step-card preview), persisted with the
   * draft so re-opening the campaign shows the same card without a fresh LLM
   * call. `previewKey` fingerprints the content the sample was generated from
   * (prompt/subject/channel/offering) — a mismatch marks the sample as stale.
   */
  previewSubject?: string;
  previewBody?: string;
  previewKey?: string;
  /**
   * Free-text refine instruction typed under the preview. Kept on the step so
   * it stays visible across wizard navigation and re-opening the campaign;
   * only applied when the user explicitly hits “Generate”.
   */
  refineInstruction?: string;
}

export interface LlmModel {
  id: string;
  model: string;
  label: string;
}

export interface PromptTestResult {
  modelId: string;
  modelLabel: string;
  subject: string;
  body: string;
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Preview & Launch types
// ---------------------------------------------------------------------------
export interface SalesRep {
  _id: string;
  name: string;
  role: string;
  company: string;
  communicationStyle: string;
  /** Sign-off used on email steps. */
  signOff: string;
  /** Sign-off used on LinkedIn DM steps; empty reuses the email one. */
  linkedinSignOff: string;
  isDefault: boolean;
}

export interface PreviewMessage {
  leadIndex: number;
  stepId: string;
  channel: SequenceChannel;
  subject: string;
  body: string;
  model: string;
}

export type LaunchStatus = 'idle' | 'previewing' | 'creating' | 'launching' | 'done' | 'error';

export interface SendingAccount {
  id: string;
  name: string;
  email?: string;
  selected: boolean;
}

export const AVAILABLE_MODELS: LlmModel[] = [
  { id: 'claude-sonnet', model: 'anthropic/claude-sonnet-4.6', label: 'Claude Sonnet 4.6' },
  { id: 'claude-haiku', model: 'anthropic/claude-haiku-4.5', label: 'Claude Haiku 4.5' },
  { id: 'gpt-5-mini', model: 'openai/gpt-5.4-mini', label: 'GPT-5.4 Mini' },
  { id: 'gpt-4o', model: 'openai/gpt-4o', label: 'GPT-4o (Legacy)' },
];

export const CHANNEL_LABELS: Record<SequenceChannel, string> = {
  email: '✉️ Email',
  linkedin_connect: '🔗 LinkedIn Connect',
  linkedin_dm: '💬 LinkedIn DM',
};

/** Languages the outreach (messages, previews, auto-replies) can be written in. */
export const OUTREACH_LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'fr', label: 'Français' },
  { code: 'ar', label: 'العربية (Arabic)' },
];

export const CHANNEL_CONDITIONS: Record<SequenceChannel, SequenceCondition[]> = {
  email: ['always', 'no_reply'],
  linkedin_connect: ['always', 'no_reply'],
  linkedin_dm: ['always', 'accepted', 'no_reply'],
};

export const CONDITION_LABELS: Record<SequenceCondition, string> = {
  always: 'Always',
  no_reply: 'No Reply',
  accepted: 'Accepted',
  not_accepted: 'Not Accepted',
  replied: 'Replied',
};

/** Lead template variables that can be used in prompts and subjects. */
export const TEMPLATE_VARIABLES: { key: string; label: string }[] = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'company', label: 'Company' },
  { key: 'job_title', label: 'Job Title' },
  { key: 'industry', label: 'Industry' },
  { key: 'employee_count', label: 'Employee Count' },
  { key: 'email', label: 'Email' },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
];

/**
 * Sender template variables — resolved per lead to that lead's assigned sales
 * rep at generation time (server: senderTemplateVars in message-generation.ts).
 * They let a prompt reference WHO is writing, which matters once a campaign runs
 * several reps (or one rep across several accounts): {{sender_name}} then fills
 * with each lead's own sender rather than a single hard-coded name.
 */
export const SENDER_VARIABLES: { key: string; label: string }[] = [
  { key: 'sender_name', label: 'Sender Name' },
  { key: 'sender_first_name', label: 'Sender First Name' },
  { key: 'sender_role', label: 'Sender Role' },
  { key: 'sender_company', label: 'Sender Company' },
];

/** Every variable the editor recognises — lead + sender. */
export const ALL_TEMPLATE_VARIABLES: { key: string; label: string }[] = [...TEMPLATE_VARIABLES, ...SENDER_VARIABLES];

/** Case and underscores don't make a token a different variable. */
const normalizeVariableKey = (key: string): string => key.toLowerCase().replace(/_/g, '');

/** Whether `{{key}}` is one of the documented variables, ignoring case/underscores. */
export function isKnownTemplateVariable(key: string): boolean {
  const n = normalizeVariableKey(key);
  return ALL_TEMPLATE_VARIABLES.some(v => normalizeVariableKey(v.key) === n);
}

/** Whether `{{key}}` is a SENDER variable (resolves from the rep), for chip colouring. */
export function isSenderTemplateVariable(key: string): boolean {
  const n = normalizeVariableKey(key);
  return SENDER_VARIABLES.some(v => normalizeVariableKey(v.key) === n);
}

/** Theme for LEAD variable chips — defined once, used by prompt-editor & variable bar. */
export const VARIABLE_CHIP_THEME = {
  bg: 'oklch(0.75 0.12 25 / 0.2)',
  color: 'oklch(0.80 0.14 25)',
  border: '1px solid oklch(0.75 0.12 25 / 0.45)',
} as const;

/**
 * Theme for SENDER variable chips — a distinct hue (violet) so an operator can
 * tell at a glance that {{sender_*}} resolves from the rep, not the lead. Same
 * shape as VARIABLE_CHIP_THEME; the editor and the variable bar both pick it for
 * a sender token.
 */
export const SENDER_CHIP_THEME = {
  bg: 'oklch(0.72 0.11 285 / 0.2)',
  color: 'oklch(0.72 0.15 285)',
  border: '1px solid oklch(0.72 0.11 285 / 0.45)',
} as const;

export const DEFAULT_PROMPTS: Record<string, string> = {
  email:
    'Write a cold outreach email to {{first_name}}, who is {{job_title}} at {{company}} ' +
    '({{industry}}, {{employee_count}} employees).\n\n' +
    'Goal: Get them curious about our offering.\n' +
    'Mention something specific about their company or role.\n' +
    'Keep it under 120 words. No generic openers.\n' +
    'Ask ONE clear question at the end to invite a reply.',
  email_followup:
    'Write a follow-up email to {{first_name}} at {{company}}.\n' +
    "They haven't replied to the previous email. Don't be pushy.\n" +
    'Add NEW value — share a specific insight, stat, or mini case study.\n' +
    'Keep it under 100 words. End with a soft CTA.',
  email_breakup:
    'Write a final breakup email to {{first_name}} at {{company}}.\n' +
    "Be respectful. Acknowledge they're busy. Leave the door open.\n" +
    'Keep it under 80 words. Friendly, not desperate.',
  linkedin_connect:
    'Write a LinkedIn connection request note to {{first_name}} ' +
    '({{job_title}} at {{company}}).\n' +
    "Keep it under 280 characters. Be casual and human. No greetings like 'Hi'.",
  linkedin_dm:
    'Write a short, friendly LinkedIn DM to {{first_name}} ' +
    '({{job_title}} at {{company}}).\n' +
    'They accepted your connection request. Be conversational, not salesy.\n' +
    'Keep it under 300 characters.',
};

const makeStepId = (): string => crypto.randomUUID();

/**
 * Derive the campaign type from the steps in the sequence.
 * - only email steps              → 'email'
 * - only LinkedIn steps           → 'linkedin'
 * - mixed (or empty fallback)     → 'multi' if both kinds exist
 * Empty sequence defaults to 'email' so the wizard / API have a stable value.
 */
export const deriveCampaignType = (sequence: Pick<SequenceStep, 'channel'>[]): CampaignType => {
  let hasEmail = false;
  let hasLinkedin = false;
  for (const step of sequence) {
    if (step.channel === 'email') hasEmail = true;
    else if (step.channel === 'linkedin_connect' || step.channel === 'linkedin_dm') hasLinkedin = true;
  }
  if (hasEmail && hasLinkedin) return 'multi';
  if (hasLinkedin) return 'linkedin';
  return 'email';
};

export const defaultEmailSequence = (): SequenceStep[] => [
  {
    id: makeStepId(),
    channel: 'email',
    delayValue: 0,
    delayUnit: 'hours',
    condition: 'always',
    prompt: DEFAULT_PROMPTS.email!,
    subject: 'Quick question about {{company}}',
  },
  {
    id: makeStepId(),
    channel: 'email',
    delayValue: 3,
    delayUnit: 'days',
    condition: 'no_reply',
    prompt: DEFAULT_PROMPTS.email_followup!,
    subject: 'Re: Quick question about {{company}}',
  },
  {
    id: makeStepId(),
    channel: 'email',
    delayValue: 5,
    delayUnit: 'days',
    condition: 'no_reply',
    prompt: DEFAULT_PROMPTS.email_breakup!,
    subject: 'Last one from me, {{first_name}}',
  },
];

/**
 * What the Sequence step says when a channel the sequence uses has no sending
 * account behind it. The old wording ("Enable email … before you can add an
 * email step") blamed the user for adding a step the wizard itself had seeded,
 * and named only one of the two ways out. Both are on offer here, and the
 * sentence describes the sequence as it actually stands. Shared so the wizard's
 * navigation gate and the Sequence step's own banner can never drift apart.
 */
export const SEQUENCE_NEEDS_EMAIL_ACCOUNT =
  'Your sequence has an email step, but no mailbox is connected for the Sales Rep sending it. Connect one, or remove the email step.';
export const SEQUENCE_NEEDS_LINKEDIN_ACCOUNT =
  'Your sequence has a LinkedIn step, but no LinkedIn account is connected for the Sales Rep sending it. Connect one, or remove the LinkedIn step.';

/**
 * True while `sequence` is still exactly the placeholder step `emptyCampaignDraft()`
 * opens with — nothing the user has shaped yet. The wizard swaps that placeholder
 * for one on a channel the user can actually send on once it knows which accounts
 * exist, and this is the guard that keeps it from ever touching real work.
 */
export const isPlaceholderSequence = (sequence: SequenceStep[]): boolean => {
  const [placeholder] = defaultEmailSequence();
  const [step, ...rest] = sequence;
  if (!placeholder || !step || rest.length > 0) return false;
  return (
    step.channel === placeholder.channel &&
    step.condition === placeholder.condition &&
    step.prompt === placeholder.prompt &&
    step.subject === placeholder.subject
  );
};

/**
 * Which channels the user has actually committed to sending on — the basis for
 * demanding a sending account per Sales Rep on the Sales Rep step. The untouched
 * placeholder step is the wizard's own guess (see isPlaceholderSequence), not a
 * channel the user chose, so it requires nothing: re-seeding to LinkedIn only
 * fires when the operator has NO mailbox at all, so an operator who has mailboxes
 * for OTHER reps but picks a LinkedIn-only rep was trapped on the Sales Rep step
 * demanding a mailbox for an email step they never added. Real requirements kick
 * in the moment the user shapes the sequence.
 */
export const channelsRequiringAccount = (sequence: SequenceStep[]): { email: boolean; linkedin: boolean } => {
  if (isPlaceholderSequence(sequence)) return { email: false, linkedin: false };
  return {
    email: sequence.some(s => s.channel === 'email'),
    linkedin: sequence.some(s => s.channel === 'linkedin_connect' || s.channel === 'linkedin_dm'),
  };
};

/**
 * Sales Reps who cannot send on ANY channel the sequence uses — no own account
 * and no fallback for either channel. This is the only account gap the wizard
 * must hard-gate on before the Summary step: the Summary's Channel Coverage
 * Warning can borrow/skip a channel for a rep that already serves the OTHER one,
 * but it shows nothing — and can resolve nothing — for a rep that serves no
 * channel at all. A rep missing just ONE required channel is a coverage gap:
 * it is deliberately let through to the Summary, where the operator either
 * borrows another rep's account ("Leads … via <rep>'s account") or acknowledges
 * the skip. Hard-gating those earlier is the bug that trapped a user whose
 * second rep had email but no LinkedIn on a campaign with a LinkedIn step —
 * they could never reach the Summary to resolve it.
 *
 * Returns the offending Sales Rep ids (empty when every rep can serve something).
 */
export const salesRepsWithoutAnyUsableAccount = (input: {
  sequence: SequenceStep[];
  aiEmployeeAssignments: { aiEmployeeId: string }[];
  emailAccountsByAie: Record<string, string[]>;
  linkedinAccountIdsByAie: Record<string, string[]>;
  channelFallbacks: ChannelFallback[];
}): string[] => {
  const need = channelsRequiringAccount(input.sequence);
  if (!need.email && !need.linkedin) return [];
  const hasFallback = (aiEmployeeId: string, channel: 'email' | 'linkedin') =>
    input.channelFallbacks.some(f => f.sourceAiEmployeeId === aiEmployeeId && f.channel === channel);
  return input.aiEmployeeAssignments
    .filter(a => {
      const hasEmail = (input.emailAccountsByAie[a.aiEmployeeId] ?? []).length > 0 || hasFallback(a.aiEmployeeId, 'email');
      const hasLinkedin =
        (input.linkedinAccountIdsByAie[a.aiEmployeeId] ?? []).length > 0 || hasFallback(a.aiEmployeeId, 'linkedin');
      return !hasEmail && !hasLinkedin;
    })
    .map(a => a.aiEmployeeId);
};

export const defaultLinkedinSequence = (): SequenceStep[] => [
  {
    id: makeStepId(),
    channel: 'linkedin_connect',
    delayValue: 0,
    delayUnit: 'hours',
    condition: 'always',
    prompt: DEFAULT_PROMPTS.linkedin_connect!,
    subject: '',
  },
  {
    id: makeStepId(),
    channel: 'linkedin_dm',
    delayValue: 15,
    delayUnit: 'minutes',
    condition: 'accepted',
    prompt: DEFAULT_PROMPTS.linkedin_dm!,
    subject: '',
  },
];

/** Default sequence for cross-channel ('multi') campaigns: kick off via LinkedIn,
 *  then follow up via email. The user can freely re-order / add steps. */
export const defaultMultiSequence = (): SequenceStep[] => [
  {
    id: makeStepId(),
    channel: 'linkedin_connect',
    delayValue: 0,
    delayUnit: 'hours',
    condition: 'always',
    prompt: DEFAULT_PROMPTS.linkedin_connect!,
    subject: '',
  },
  {
    id: makeStepId(),
    channel: 'email',
    delayValue: 2,
    delayUnit: 'days',
    condition: 'no_reply',
    prompt: DEFAULT_PROMPTS.email!,
    subject: 'Quick question about {{company}}',
  },
  {
    id: makeStepId(),
    channel: 'linkedin_dm',
    delayValue: 1,
    delayUnit: 'days',
    condition: 'accepted',
    prompt: DEFAULT_PROMPTS.linkedin_dm!,
    subject: '',
  },
  {
    id: makeStepId(),
    channel: 'email',
    delayValue: 3,
    delayUnit: 'days',
    condition: 'no_reply',
    prompt: DEFAULT_PROMPTS.email_followup!,
    subject: 'Re: Quick question about {{company}}',
  },
];

// ---------------------------------------------------------------------------
// Campaign Draft
// ---------------------------------------------------------------------------
export interface SalesRepAssignment {
  aiEmployeeId: string;
  /** Integer 0–100, sum across a campaign's assignments must equal 100. */
  percentage: number;
}

/**
 * The Sales Rep a message PREVIEW should sign as: the campaign's own selected
 * rep — never the user's global default, and never the server's "Sarah Miller"
 * fallback that shows when the preview forgets to say who is sending. With
 * several reps the first assignment stands in for the single sample message; the
 * deprecated single id is the fallback for an old draft that has no assignments.
 * Undefined only when no rep is chosen yet — then the server default applies.
 */
export function previewSenderId(draft: {
  aiEmployeeAssignments?: readonly { aiEmployeeId: string }[];
  aiEmployeeId?: string | null;
}): string | undefined {
  return draft.aiEmployeeAssignments?.[0]?.aiEmployeeId ?? draft.aiEmployeeId ?? undefined;
}

/**
 * The `/api/prompt/test` request body. Kept here (pure, no rendering) so the one
 * field this preview used to drop — `aiEmployeeId` — is covered by a test:
 * without it the server falls back to the user's default rep, and to a
 * hard-coded name when there is none, so a preview signed off as the wrong
 * person.
 */
export function buildPreviewRequestBody(args: {
  step: Pick<SequenceStep, 'channel' | 'subject' | 'subjectPrompt'>;
  offering: Pick<OfferingConfig, 'name' | 'description' | 'talkingPoints'>;
  effectivePrompt: string;
  lead: Record<string, string>;
  language?: string;
  aiEmployeeId?: string;
}): Record<string, unknown> {
  const { step, offering, effectivePrompt, lead, language, aiEmployeeId } = args;
  return {
    prompt: effectivePrompt,
    channel: step.channel,
    subject: step.subject,
    subjectPrompt: step.subjectPrompt || undefined,
    lead,
    language: language || undefined,
    // The campaign's selected rep, so the preview signs off as that rep rather
    // than the server's default fallback.
    aiEmployeeId: aiEmployeeId || undefined,
    offering: { name: offering.name, description: offering.description, talking_points: offering.talkingPoints },
  };
}

/**
 * Cross-AIE channel fallback. When the source AIE has no account for the given
 * channel, the fallback AIE's account is used to send the borrowed channel's
 * steps. The persona / message author stays the source AIE.
 */
export interface ChannelFallback {
  sourceAiEmployeeId: string;
  channel: 'email' | 'linkedin';
  fallbackAiEmployeeId: string;
}

export interface CampaignDraft {
  _id?: string;
  /**
   * Set once the campaign has actually been launched (Instantly/Unipile
   * external id). Only used client-side to skip the destructive
   * bulk-replace-leads autosave call once real send progress exists — the
   * server enforces the same rule independently.
   */
  externalCampaignId?: string;
  /**
   * Campaign status as of load, for the same destructive-autosave guard as
   * `externalCampaignId` above — undefined for a not-yet-created draft (safe
   * to save), only ever fetched (and checked) when editing an existing
   * campaign.
   */
  status?: string;
  /**
   * Set once the campaign has sent for the first time. Used client-side to route
   * a sender-account addition through /sender-accounts (which only adds accounts
   * and re-balances still-uncontacted leads) instead of /sales-reps (which is
   * fixed per rep once launched). The server enforces the same rule.
   */
  launchedAt?: string;
  name: string;
  campaignType: CampaignType | null;
  offering: OfferingConfig;
  leads: LeadRecord[];
  schedule: ScheduleConfig;
  sequence: SequenceStep[];
  emailAccounts: string[];
  linkedinAccountIds: string[];
  llmModel?: LlmModel | null;
  replyLlmModel?: LlmModel | null;
  /** @deprecated kept for backward compatibility — new code uses `aiEmployeeAssignments`. */
  aiEmployeeId?: string | null;
  /** Multiple AIEs with percentage split. Sum of percentages = 100. */
  aiEmployeeAssignments: SalesRepAssignment[];
  /** Selected email accounts grouped per AIE. */
  emailAccountsByAie: Record<string, string[]>;
  /** Selected LinkedIn account ids grouped per AIE. */
  linkedinAccountIdsByAie: Record<string, string[]>;
  /** Optional cross-AIE channel fallbacks (delegate sending to another AIE's account). */
  channelFallbacks: ChannelFallback[];
  autoReply?: boolean;
  replyPrompt?: string;
  /**
   * When true, every lead in this campaign — including CSV imports — is
   * pushed to and continuously updated in the connected CRM after each sync.
   * When false (default), CSV-only leads are explicitly skipped; leads that
   * originated from the CRM are still kept in sync.
   */
  crmAutoSync?: boolean;
  apolloSearchInput?: Record<string, unknown>;
  /**
   * Outreach language (ISO code, see OUTREACH_LANGUAGES). All generated
   * messages, previews and auto-replies are written in this language.
   * Defaults to English.
   */
  language?: string;
  /** Project this campaign belongs to — scopes lead-sourcing dedup. Undefined = the user's default project (no dedup). */
  projectId?: string;
}

const makeTime = (h: number, m: number): Date => {
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

export const defaultSchedule = (): ScheduleConfig => ({
  timezone: 'Europe/Berlin',
  startTime: makeTime(9, 0),
  endTime: makeTime(17, 0),
  sendDays: WEEKDAYS.slice(0, 5), // Mon-Fri
  dailyLimit: 30,
  autoDmOnAccept: true,
});

export const emptyCampaignDraft = (): CampaignDraft => ({
  name: '',
  // Default to 'email' — the wizard infers the actual type from the sequence
  // at save / launch time, but we never want a null value flowing into the API.
  campaignType: 'email',
  offering: { name: '', description: '', talkingPoints: [] },
  leads: [],
  schedule: defaultSchedule(),
  // Start every new campaign with one Email step so the Sequence builder is
  // immediately useful and the derived campaign type is valid from the start.
  sequence: defaultEmailSequence().slice(0, 1),
  emailAccounts: [],
  linkedinAccountIds: [],
  llmModel: AVAILABLE_MODELS[0] ?? null,
  replyLlmModel: AVAILABLE_MODELS[0] ?? null,
  aiEmployeeId: null,
  aiEmployeeAssignments: [],
  emailAccountsByAie: {},
  linkedinAccountIdsByAie: {},
  channelFallbacks: [],
  autoReply: false,
  replyPrompt: '',
  crmAutoSync: false,
  apolloSearchInput: undefined,
});
